import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  BookOpen,
  Bot,
  Camera,
  ChevronLeft,
  ChevronRight,
  Database,
  GitCompare,
  Layers,
  ListChecks,
  LogOut,
  MessageSquare,
  ScrollText,
  Settings,
  Shield,
  UserCircle,
  Users,
  X,
} from "lucide-react";
import { useLogoutFunction } from "@propelauth/react";
import { OrgSwitcher } from "./OrgSwitcher";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { useRole } from "../hooks/useRole";
import { ConversationViewsNav } from "./views/ConversationViewsNav";

interface DrawerNavProps {
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

export function DrawerNav({
  isCollapsed,
  onToggleCollapsed,
  isMobileOpen,
  onCloseMobile,
}: DrawerNavProps) {
  const location = useLocation();
  const { user, role, isDemo } = useRole();
  const logout = useLogoutFunction();

  const navItems = [
    {
      to: "/threads",
      label: "Threads",
      shortLabel: "Threads",
      icon: MessageSquare,
      active: location.pathname === "/threads" || location.pathname.startsWith("/threads/"),
    },
    {
      to: "/batches",
      label: "Batches",
      shortLabel: "Batches",
      icon: Layers,
      active: location.pathname === "/batches" || location.pathname.startsWith("/batches/"),
    },
    {
      to: "/runs",
      label: "Runs",
      shortLabel: "Runs",
      icon: Activity,
      active: location.pathname === "/runs" || location.pathname.startsWith("/runs/"),
    },
    {
      to: "/test-suites",
      label: "Test Suites",
      shortLabel: "Suites",
      icon: ListChecks,
      active: location.pathname === "/test-suites" || location.pathname.startsWith("/test-suites/"),
    },
    {
      to: "/test-runs",
      label: "Test Runs",
      shortLabel: "Test Runs",
      icon: Activity,
      active: location.pathname === "/test-runs" || location.pathname.startsWith("/test-runs/"),
    },
    {
      to: "/snapshots",
      label: "Snapshots",
      shortLabel: "Snapshots",
      icon: Camera,
      active: location.pathname === "/snapshots" || location.pathname.startsWith("/snapshots/"),
    },
    {
      to: "/test-runs/compare",
      label: "Compare",
      shortLabel: "Compare",
      icon: GitCompare,
      active: location.pathname === "/test-runs/compare",
    },
    {
      to: "/rules",
      label: "Rules",
      shortLabel: "Rules",
      icon: ScrollText,
      active: location.pathname === "/rules" || location.pathname.startsWith("/rules/"),
    },
    {
      to: "/knowledge",
      label: "Knowledge",
      shortLabel: "Knowledge",
      icon: BookOpen,
      active: location.pathname === "/knowledge" || location.pathname.startsWith("/knowledge/"),
    },
    {
      to: "/guardrails",
      label: "Guardrails",
      shortLabel: "Guardrails",
      icon: Shield,
      active: location.pathname === "/guardrails" || location.pathname.startsWith("/guardrails/"),
    },
    {
      to: "/agents",
      label: "Agents",
      shortLabel: "Agents",
      icon: Bot,
      active: location.pathname === "/agents" || location.pathname.startsWith("/agents/"),
    },
    {
      to: "/users",
      label: "Users",
      shortLabel: "Users",
      icon: Users,
      active: location.pathname === "/users" || location.pathname.startsWith("/users/"),
    },
    {
      to: "/settings",
      label: "Settings",
      shortLabel: "Settings",
      icon: Settings,
      active: location.pathname === "/settings",
    },
  ];

  const handleSignOut = () => {
    if (isDemo) {
      sessionStorage.removeItem("silvestre_demo_mode");
      window.location.reload();
    } else {
      logout(true);
    }
  };

  const renderContent = (collapsed: boolean, isMobile: boolean) => (
    <div className="h-full flex flex-col justify-between bg-white dark:bg-stone-900 text-stone-800 dark:text-stone-200 border-r border-stone-200 dark:border-stone-800 select-none transition-colors">
      {/* Brand Header & Top Nav */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div
          className={`h-14 flex items-center border-b border-stone-100 dark:border-stone-800/80 px-3.5 ${
            collapsed ? "justify-center" : "justify-between"
          }`}
        >
          <Link
            to="/threads"
            onClick={isMobile ? onCloseMobile : undefined}
            className="flex items-center gap-2.5 overflow-hidden"
            title="Silvestre Knowledge Platform"
          >
            <div className="w-8 h-8 rounded-lg bg-stone-900 dark:bg-stone-800 text-white flex items-center justify-center shrink-0 shadow-xs">
              <Database className="w-4 h-4 text-emerald-400" />
            </div>
            {!collapsed && (
              <div className="flex flex-col min-w-0">
                <span className="font-serif font-bold text-sm tracking-tight text-stone-900 dark:text-stone-100 leading-none">
                  Silvestre
                </span>
                <span className="text-[9px] uppercase tracking-widest text-stone-400 dark:text-stone-500 font-semibold mt-0.5">
                  Knowledge Platform
                </span>
              </div>
            )}
          </Link>

          {isMobile && (
            <button
              onClick={onCloseMobile}
              className="p-1.5 text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-100 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="p-2 space-y-1 overflow-y-auto flex-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isConversations = item.to === "/threads";
            return (
              <Fragment key={item.to}>
                <Link
                  to={item.to}
                  onClick={isMobile ? onCloseMobile : undefined}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium transition ${
                    item.active
                      ? "bg-stone-900 text-white dark:bg-stone-800 dark:text-white shadow-2xs font-semibold"
                      : "text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100/75 dark:hover:bg-stone-800/60"
                  } ${collapsed ? "justify-center px-2" : ""}`}
                  title={item.label}
                >
                  <Icon
                    className={`w-4 h-4 shrink-0 ${
                      item.active ? "text-white" : "text-stone-500 dark:text-stone-400"
                    }`}
                  />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </Link>
                {isConversations && !collapsed && (
                  <ConversationViewsNav onCloseMobile={isMobile ? onCloseMobile : undefined} />
                )}
              </Fragment>
            );
          })}
        </nav>
      </div>

      {/* Relocated Top Navbar Controls: Org Switcher, User Badge, Theme, Logout */}
      <div className="p-2 border-t border-stone-200 dark:border-stone-800/80 bg-stone-50/50 dark:bg-stone-950/40 space-y-2 shrink-0">
        {!collapsed ? (
          <>
            {/* Multi-tenant Org Switcher */}
            <div className="w-full">
              <OrgSwitcher />
            </div>

            {/* User Profile & Demo Badge */}
            <div className="flex items-center justify-between px-1.5 py-1 text-xs text-stone-600 dark:text-stone-300">
              <div className="flex items-center gap-2 min-w-0">
                <UserCircle className="w-4 h-4 text-stone-400 dark:text-stone-500 shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-stone-800 dark:text-stone-200 truncate text-[11px] leading-tight">
                    {user?.firstName || user?.email || "Operator"}
                  </span>
                  <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono">
                    {role}
                  </span>
                </div>
              </div>

              {isDemo && (
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.removeItem("silvestre_demo_mode");
                    const url = new URL(window.location.href);
                    url.searchParams.delete("demo");
                    window.location.href = url.pathname;
                  }}
                  className="px-1.5 py-0.5 text-[9px] font-mono font-semibold bg-amber-100 dark:bg-amber-950 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-800 rounded transition"
                  title="Click to exit sandbox demo mode"
                >
                  DEMO
                </button>
              )}
            </div>

            {/* Theme switcher + Sign out + Collapse */}
            <div className="flex items-center justify-between pt-1 border-t border-stone-200/60 dark:border-stone-800/60">
              <div className="flex items-center gap-1">
                <ThemeSwitcher />
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="p-1.5 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition"
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>

              {!isMobile && (
                <button
                  type="button"
                  onClick={onToggleCollapsed}
                  className="p-1.5 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition"
                  title="Collapse sidebar"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
            </div>
          </>
        ) : (
          /* Collapsed Icons Only */
          <div className="flex flex-col items-center gap-2 py-1">
            <ThemeSwitcher />
            <button
              type="button"
              onClick={handleSignOut}
              className="p-1.5 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
            {!isMobile && (
              <button
                type="button"
                onClick={onToggleCollapsed}
                className="p-1.5 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition"
                title="Expand sidebar"
                aria-label="Expand sidebar"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Drawer */}
      <aside
        className={`hidden md:block shrink-0 transition-all duration-300 ease-in-out h-screen ${
          isCollapsed ? "w-16" : "w-60"
        }`}
      >
        {renderContent(isCollapsed, false)}
      </aside>

      {/* Mobile Drawer Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          <div
            className="fixed inset-0 bg-stone-900/40 dark:bg-black/60 backdrop-blur-xs transition-opacity"
            onClick={onCloseMobile}
          />
          <div className="relative w-64 max-w-[80vw] h-full shadow-2xl z-10">
            {renderContent(false, true)}
          </div>
        </div>
      )}
    </>
  );
}
